import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box, Button, TextField, Checkbox, FormControl, FormControlLabel, Typography, Divider, Alert } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FillInBlanksQuestion from './FillInBlanksQuestion';
import FillInBlanksPreview from './FillInBlanksPreview';
import { generateFillInBlanksQuestions } from '../../../services/aiExerciseService';

interface QuestionConfig {
  id: string;
  text: string;
  answers: string[][];
  hint: string;
  aiCheckEnabled: boolean;
  voiceCode?: string;  
}

interface FillInBlanksConfigProps {
  initialConfig?: any;
  onConfigChange: (config: any) => void;
}

const FillInBlanksConfig: React.FC<FillInBlanksConfigProps> = ({ initialConfig = {}, onConfigChange }) => {
  console.log('Render FillInBlanksConfig');

  const [questions, setQuestions] = useState<QuestionConfig[]>(initialConfig.questions || []);
  const [language, setLanguage] = useState(initialConfig.language || 'fi');
  const [isContinuous, setIsContinuous] = useState(initialConfig.isContinuous || false);
  const [useTextToSpeech, setUseTextToSpeech] = useState(initialConfig.useTextToSpeech || false);
  
  // AI generation state
  const [customPrompt, setCustomPrompt] = useState('');
  const [questionCount, setQuestionCount] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const config = {
      exerciseType: 'fill_in_the_blanks',
      language,
      isContinuous,
      useTextToSpeech,
      questions,
    };
    onConfigChange(config);
  }, [language, isContinuous, useTextToSpeech, questions]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const addQuestion = useCallback(() => {
    setQuestions(prev => [
      ...prev,
      { id: uuidv4(), text: '', answers: [[]], hint: '', aiCheckEnabled: false, voiceCode: 'en-US-JennyNeural' },
    ]);
  }, []);

  const updateQuestion = useCallback((index: number, updatedQuestion: QuestionConfig) => {
    setQuestions(prev => {
      const updatedQuestions = [...prev];
      updatedQuestions[index] = { ...updatedQuestion, aiCheckEnabled: updatedQuestion.aiCheckEnabled ?? false };
      return updatedQuestions;
    });
  }, []);

  const removeQuestion = useCallback((index: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleGenerateQuestions = useCallback(async () => {
    if (!customPrompt.trim()) {
      setGenerationError('Anna ohje kysymysten luomiseen');
      return;
    }

    setIsGenerating(true);
    setGenerationError('');

    try {
      const response = await generateFillInBlanksQuestions(customPrompt, questionCount);
      
      if (response.data) {
        const generatedQuestions = response.data.map((q: any) => ({
          id: q.id || uuidv4(),
          text: q.text || '',
          answers: q.answers || [[]],
          hint: q.hint || '',
          aiCheckEnabled: false,
          voiceCode: 'en-US-JennyNeural',
        }));
        
        setQuestions(prev => [...prev, ...generatedQuestions]);
        setCustomPrompt(''); // Clear the prompt after successful generation
      } else {
        setGenerationError('Kysymysten luominen epäonnistui');
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      setGenerationError('Virhe kysymysten luomisessa. Yritä uudelleen.');
    } finally {
      setIsGenerating(false);
    }
  }, [customPrompt, questionCount]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Tyhjän kohdan täyttö - Asetukset</Typography>
      <FormControl fullWidth margin="normal">
        <TextField
          label="Kieli"
          variant="outlined"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        />
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={isContinuous}
            onChange={() => setIsContinuous(!isContinuous)}
          />
        }
        label="Onko jatkoa"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={useTextToSpeech}
            onChange={() => setUseTextToSpeech(!useTextToSpeech)}
          />
        }
        label="Käytä tekstistä puheeksi"
      />

      <Divider sx={{ my: 3 }} />

      {/* AI Question Generation Section */}
      <Box mt={3} mb={3}>
        <Typography variant="h6" gutterBottom>
          Luo kysymyksiä tekoälyn avulla
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Anna ohje siitä, millaisia kysymyksiä haluat luoda. Esimerkiksi:
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          • "Historia: Luo kysymyksiä toisen maailmansodan aiheista"
          • "Kieli: Luo italiankielisiä verbitäyttötehtäviä"
          • "Tiede: Luo kysymyksiä lämpötilasta ja fysiikasta"
        </Typography>
        
        <FormControl fullWidth margin="normal">
          <TextField
            label="Ohje kysymysten luomiseen"
            variant="outlined"
            multiline
            rows={3}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Esim: Luo kysymyksiä Suomen historiasta, keskity itsenäistymiseen ja sotiin"
          />
        </FormControl>

        <FormControl margin="normal" sx={{ minWidth: 120, mr: 2 }}>
          <TextField
            label="Kysymysten määrä"
            type="number"
            variant="outlined"
            size="small"
            value={questionCount}
            onChange={(e) => setQuestionCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 20 }}
          />
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleGenerateQuestions}
          disabled={isGenerating || !customPrompt.trim()}
          sx={{ ml: 1 }}
        >
          {isGenerating ? 'Luodaan...' : 'Luo kysymyksiä'}
        </Button>

        {generationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {generationError}
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box mt={2} mb={2}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addQuestion}
        >
          Lisää kysymys manuaalisesti
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {questions.map((question, index) => (
        <FillInBlanksQuestion
          key={question.id}
          index={index}
          question={question} // Pass the question object that includes voiceCode
          onQuestionChange={updateQuestion}
          onRemoveQuestion={removeQuestion}
        />
      ))}

      <Box mt={4}>
        <Typography variant="h6">Esikatselu</Typography>
        <FillInBlanksPreview
          config={{
            questions: questions,
            isContinuous,
          }}
        />
      </Box>
    </Box>
  );
};

export default FillInBlanksConfig;
